import { useClientTimeZone } from '@/hooks/useClientTimeZone';
import useTranslation from '@/hooks/useTranslation';
import { DateTimeFormat, sanitizeIcu } from '@/utils/dateUtils';
import type { FCl } from 'react-fc';

export const useDateTimeFormat = (extras: Intl.DateTimeFormatOptions = {}, precision?: PrecisionType) => {
    const { lang } = useTranslation();
    const timeZone = useClientTimeZone();
    return DateTimeFormat(lang, {
        ...generateFormat(precision),
        timeZone,
        ...extras,
    });
};
const generateFormat = (precision?: PrecisionType) => {
    const format: Record<string, string> = {};
    switch (precision) {
        default:
        case 'weekday':
            format['weekday'] = 'short';
        // eslint-disable-next-line no-fallthrough
        case 'day':
            format['day'] = 'numeric';
        // eslint-disable-next-line no-fallthrough
        case 'month':
            format['month'] = 'short';
        // eslint-disable-next-line no-fallthrough
        case 'year':
            format['year'] = 'numeric';
            break;
    }
    return format;
};

/**
 * A locale-formatted date string wrapped in `<time/>`.
 */
export const LocalizedDate: FCl<IProps> = ({ date, className, precision, precisionOverrides = {} }) => {
    const overrides = { ...precisionOverrides };
    if (!date.includes('T') && !overrides.timeZone) {
        // Date without time such as '2024-01-01' will be interpreted 00:00 UTC
        overrides.timeZone = 'UTC';
    }
    const dt = useDateTimeFormat(overrides, precision);
    return (
        <time dateTime={date} className={className} title={date}>
            {sanitizeIcu(dt.format(new Date(date)))}
        </time>
    );
};

interface IProps {
    date: string;
    precision?: PrecisionType;
    precisionOverrides?: Intl.DateTimeFormatOptions;
}

export enum PrecisionType {
    weekday = 'weekday',
    day = 'day',
    month = 'month',
    year = 'year',
}
